/*
 * NC PDP — galería (fade en desktop, swipe con snap + indicador en móvil), selector de variantes, sticky ATC
 * y ancla de rating. PROTEGIDO (3×2): se conserva intacto el evento `nc:variant-change` que nc-bundle escucha,
 * los selectores [data-nc-option-value] / [data-nc-variant-input] y el sincronizado de precio/compare-at/escasez.
 */
class NCPdpGallery {
  constructor(root) {
    this.root = root;
    this.main = root.querySelector('[data-nc-gallery-main]');
    this.slides = Array.from(root.querySelectorAll('[data-nc-gallery-slide]'));
    this.thumbs = Array.from(root.querySelectorAll('[data-nc-gallery-thumb]'));
    this.dots = Array.from(root.querySelectorAll('[data-nc-gallery-dot]'));
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.ticking = false;
    root.__ncGallery = this;

    [...this.thumbs, ...this.dots].forEach((btn) => {
      btn.addEventListener('click', () => this.show(parseInt(btn.dataset.index, 10)));
    });

    if (this.main) {
      this.main.addEventListener(
        'scroll',
        () => {
          if (!this.isSwipe() || this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            this.ticking = false;
            const i = Math.round(this.main.scrollLeft / Math.max(1, this.main.clientWidth));
            if (i !== this.index && this.slides[i]) {
              this.index = i;
              this.paint(i);
            }
          });
        },
        { passive: true }
      );
      window.addEventListener(
        'resize',
        () => {
          if (this.isSwipe()) this.main.scrollTo({ left: this.index * this.main.clientWidth, behavior: 'auto' });
        },
        { passive: true }
      );
      if (this.isSwipe() && this.index > 0) this.main.scrollTo({ left: this.index * this.main.clientWidth, behavior: 'auto' });
    }
  }

  isSwipe() {
    return !!this.main && getComputedStyle(this.main).display === 'flex';
  }

  paint(index) {
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
    this.thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
      thumb.setAttribute('aria-pressed', String(i === index));
    });
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  show(index) {
    if (!this.slides[index]) return;
    this.index = index;
    this.paint(index);
    if (this.isSwipe()) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.main.scrollTo({ left: index * this.main.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
    }
  }
}

function formatMoney(cents) {
  return '$' + (cents / 100).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function initVariantPicker(root, sectionId) {
  const variants = (window.__ncPdpVariants || {})[sectionId];
  const inventory = (window.__ncPdpInventory || {})[sectionId] || {};
  if (!variants || !variants.length) return;

  const optionButtons = Array.from(root.querySelectorAll('[data-nc-option-value]'));
  if (!optionButtons.length) return;

  const optionCount = variants[0].options.length;
  const selected = new Array(optionCount).fill(null);

  optionButtons.forEach((btn) => {
    const group = btn.closest('.nc-pdp__option-group');
    if (btn.classList.contains('is-selected')) {
      selected[parseInt(btn.dataset.optionIndex, 10)] = btn.dataset.value;
    }
    btn.addEventListener('click', () => {
      const idx = parseInt(btn.dataset.optionIndex, 10);
      selected[idx] = btn.dataset.value;
      group.querySelectorAll('[data-nc-option-value]').forEach((b) => {
        b.classList.toggle('is-selected', b === btn);
        b.setAttribute('aria-pressed', String(b === btn));
      });
      const current = group.querySelector('[data-nc-option-current]');
      if (current) current.textContent = btn.dataset.value;
      applySelection();
    });
  });

  function applySelection() {
    const match = variants.find((v) => v.options.every((val, i) => selected[i] === null || val === selected[i]));
    if (!match) return;
    root.dispatchEvent(new CustomEvent('nc:variant-change', { bubbles: true, detail: { variantId: match.id } }));

    const variantInput = root.querySelector('[data-nc-variant-input]');
    if (variantInput) {
      variantInput.value = match.id;
      variantInput.disabled = !match.available;
    }

    const image = match.featured_image;
    if (image) {
      const gallery = root.querySelector('[data-nc-pdp-gallery]');
      const slides = Array.from(gallery?.querySelectorAll('[data-nc-gallery-slide]') || []);
      const index = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(match.featured_media?.id));
      if (index >= 0 && gallery && gallery.__ncGallery) gallery.__ncGallery.show(index);
      const stickyImage = document.querySelector('.nc-pdp__sticky-img');
      if (stickyImage && image.src) {
        stickyImage.src = image.src + (image.src.includes('?') ? '&' : '?') + 'width=120';
        stickyImage.removeAttribute('srcset');
      }
    }
    const url = new URL(location.href);
    url.searchParams.set('variant', match.id);
    history.replaceState(null, '', url);
    const stickyButton = document.querySelector('[data-nc-sticky-atc-btn]');
    if (stickyButton) {
      stickyButton.disabled = !match.available;
      stickyButton.textContent = match.available ? 'Agregar' : 'Agotado';
    }
    const material = root.querySelector('[data-nc-material]');
    if (material) material.textContent = match.options.includes('Oro') ? 'Acero 316L con laminado de oro 18K' : 'Acero inoxidable 316L';
    const badge = root.querySelector('[data-nc-pdp-discount]');
    if (badge) {
      badge.hidden = !(match.compare_at_price > match.price);
      badge.textContent = Math.round(((match.compare_at_price - match.price) * 100) / match.compare_at_price) + '% OFF';
    }

    const priceEl = root.querySelector('[data-nc-price]');
    if (priceEl) priceEl.textContent = formatMoney(match.price);

    const compareEl = root.querySelector('[data-nc-compare]');
    const saveEl = root.querySelector('[data-nc-save]');
    const hasDiscount = match.compare_at_price && match.compare_at_price > match.price;
    if (compareEl) {
      if (hasDiscount) {
        compareEl.textContent = formatMoney(match.compare_at_price);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
    if (saveEl) {
      if (hasDiscount) {
        saveEl.textContent = 'Ahorras ' + formatMoney(match.compare_at_price - match.price);
        saveEl.hidden = false;
      } else {
        saveEl.hidden = true;
      }
    }

    const scarcityEl = root.querySelector('[data-nc-scarcity]');
    if (scarcityEl) {
      let label = '';
      const inv = inventory[match.id];
      if (inv && inv.managed && inv.policy === 'deny') {
        const qty = inv.quantity;
        if (qty > 0 && qty <= 2) label = 'Últimas unidades';
        else if (qty > 2 && qty <= 5) label = 'Quedan pocas unidades';
      }
      scarcityEl.textContent = label;
      scarcityEl.hidden = !label;
    }

    const stickyPrice = document.querySelector('[data-nc-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = formatMoney(match.price);

    const atcButtons = root.querySelectorAll('.nc-pdp__atc');
    atcButtons.forEach((btn) => {
      btn.disabled = !match.available;
    });
  }
  applySelection();
}

function initStickyAtc() {
  const form = document.querySelector('[data-nc-pdp-form]');
  const sticky = document.querySelector('[data-nc-sticky-atc]');
  const stickyBtn = document.querySelector('[data-nc-sticky-atc-btn]');
  if (!form || !sticky) return;

  if (stickyBtn) {
    stickyBtn.addEventListener('click', () => {
      const realBtn = form.querySelector('.nc-pdp__atc');
      if (realBtn) realBtn.click();
    });
  }

  /*
   * Solo aparece cuando el usuario YA PASÓ el CTA principal (el formulario quedó por encima del viewport) y
   * nunca sobre COMPLETA TU 3×2. Mientras el CTA esté visible o todavía por debajo del fold, no se muestra.
   */
  let formPassed = false;
  let bundleVisible = false;
  const sync = () => {
    sticky.hidden = !formPassed || bundleVisible;
  };
  new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        formPassed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      });
      sync();
    },
    { threshold: 0 }
  ).observe(form);
  const bundle = document.querySelector('nc-bundle');
  if (bundle) {
    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          bundleVisible = entry.isIntersecting;
        });
        sync();
      },
      { threshold: 0 }
    ).observe(bundle);
  }
}

function initRatingAnchor() {
  const link = document.querySelector('.nc-pdp__rating[href^="#"]');
  if (!link) return;
  link.addEventListener('click', (event) => {
    const target = document.querySelector(link.getAttribute('href'));
    if (!target) return;
    // El scroller del theme no es `window` en desktop (.page-wrapper); scrollIntoView sube por el ancestro
    // que realmente hace scroll. `instant` es fiable aquí.
    event.preventDefault();
    target.scrollIntoView({ behavior: 'instant', block: 'start' });
    history.replaceState(null, '', link.getAttribute('href'));
  });
}

document.querySelectorAll('[data-nc-pdp-gallery]').forEach((el) => new NCPdpGallery(el));
initRatingAnchor();

document.querySelectorAll('[data-nc-pdp-form]').forEach((form) => {
  const root = form.closest('.nc-pdp__info') ? form.closest('.nc-pdp') : document;
  initVariantPicker(root, form.dataset.sectionId);
});

initStickyAtc();
